import styles from './StreakHeat.module.css';

type StreakHeatProps = {
  /** One entry per cell, true = filled (a day was logged). */
  cells: boolean[];
  /** Which cell is "today" — outlined + tinted yellow regardless of its cells[i] value. */
  todayIndex?: number;
  /** Grid column count (default 7 for a week-based heatmap). */
  columns?: number;
  className?: string;
};

/**
 * N-day streak heatmap. Supports arbitrary cell counts + arbitrary
 * column layouts (7 for a week, 14 for two weeks, 28 for a 4-week
 * view as in forward-momentum.jsx).
 *
 * Filled cells (on) render with --signal marine per palette-ink-marine.css.
 * Today cell renders with --today yellow + --ink-900 outline — wins over
 * on-ness when both apply (today takes precedence regardless of cells[i]).
 *
 * Day-0 variant (empty streak, "Start your streak" copy) is a sibling
 * component shipped in step 6 (StreakTile), not a variant of this
 * primitive. This primitive is just the grid.
 */
export function StreakHeat({
  cells,
  todayIndex,
  columns = 7,
  className,
}: StreakHeatProps) {
  return (
    <div
      className={[styles.heat, className].filter(Boolean).join(' ')}
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      aria-label={`${cells.filter(Boolean).length} of ${cells.length} days logged`}
    >
      {cells.map((filled, i) => (
        <div
          key={i}
          className={[
            styles.cell,
            filled ? styles.on : '',
            i === todayIndex ? styles.today : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      ))}
    </div>
  );
}
